import { FC } from 'react';
import { useTranslate } from 'hooks/useTranslate';

import CellData from 'modules/_shared/components/Tables/CellData';

import { formatEnvelopeDate } from 'modules/envelopes/utils/formatEnvelopeDate';
import { envelopeStatus } from 'modules/envelopes/components/EnvelopesTable/envelopeStatus';

type Signer = {
  name?: string;
  email?: string;
  status?: string;
  signedDateTime?: string | number | Date;
};

type EnvelopeDetailsSignersCardProps = {
  allSigners: Signer[];
};

const EnvelopeDetailsSignersCard: FC<EnvelopeDetailsSignersCardProps> = ({
  allSigners,
}) => {
  const { t } = useTranslate();

  const getStatusColor = (status: string) =>
    envelopeStatus[status as keyof typeof envelopeStatus].badge ||
    envelopeStatus._nostatus.badge;

  const getStatusIcon = (status: string) =>
    envelopeStatus[status as keyof typeof envelopeStatus].icon ||
    envelopeStatus._nostatus.icon;

  const getStatusLabel = (status: string) =>
    t(envelopeStatus[status as keyof typeof envelopeStatus].label);

  return (
    <div className="card rounded-xl">
      <div className="card-inner">
        <h6 className="card-title mb-3">{t('Signers')}</h6>
        <p className="text-soft mb-4">
          {t('TrackSigningStatusOfEachRecipient')}
        </p>

        <div className="space-y-3">
          {allSigners.length > 0 ? (
            allSigners.map((signer, index) => (
              <div
                key={signer.email || index}
                className="border rounded-lg p-3"
              >
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gx-3 gy-2">
                    <CellData.Partner
                      name={signer.name}
                      // @ts-ignore
                      email={signer.email || ''}
                      image=""
                      url=""
                      avatarClassName="bg-primary rounded-circle"
                    />
                  </div>

                  <div className="d-flex align-items-center justify-content-between w-250px">
                    <div className="text-end">
                      <div className="d-flex align-items-center">
                        <em
                          className={`icon ni fs-16px mr-1 ${getStatusIcon(
                            signer.status || '_nostatus'
                          )}`}
                        />
                        <span
                          className={`badge badge-pill ${getStatusColor(
                            signer.status || '_nostatus'
                          )}`}
                        >
                          {getStatusLabel(signer.status || '_nostatus')}
                        </span>
                      </div>

                      {signer.signedDateTime && (
                        <span className="text-muted mt-1 mb-0 fs-12px">
                          {t('Signed')}:{' '}
                          {formatEnvelopeDate(signer.signedDateTime)}
                        </span>
                      )}
                    </div>

                    {signer.status === 'PENDING' && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                      >
                        <em className="icon ni ni-send mr-2" />
                        {t('Remind')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-4">
              <div className="mb-3">
                <em className="icon ni ni-users fs-1 text-soft" />
              </div>
              <h6>{t('No Signers')}</h6>
              <p className="text-soft">
                {t('No signers have been added to this envelope')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnvelopeDetailsSignersCard;
